// React
import { ReactElement, ReactNode, useEffect, useState } from 'react';
// Next
import Head from 'next/head';
// Modules
import classNames from 'classnames/bind';
import ResizeObserver from 'react-resize-observer';
import { SkipNavContent, SkipNavLink } from '@reach/skip-nav';
import { Slide, ToastContainer } from 'react-toastify';
// Layout
import { MobileSkeleton } from '@utmb/features/layout/navigation-public/MobileSkeleton';
// Features
import RegistrationSkeleton from '@utmb/features/my-utmb/registration-skeleton/RegistrationSkeleton';
import { Footer } from '@utmb/features/footer/footer';
import { Mobile } from '@utmb/features/layout/navigation-public/mobile';
import { TopBar } from '@utmb/features/layout/navigation-public/desktop-utils/top-bar';
// Design System
import BrandSkeleton from '@utmb/design-system/my-utmb-brand/BrandSkeleton';
import Navigation from '@utmb/design-system/my-utmb-navigation/Navigation';
import { NavigationSkeleton } from '@utmb/design-system/my-utmb-navigation/NavigationSkeleton';
import { MobileRegistrationNavigation } from '@utmb/design-system/my-utmb-navigation/MobileRegistrationNavigation';
// Contexts/Hooks
import { useCloudinary } from '@utmb/design-system/use-cloudinary/UseCloudinary';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useDashboardNavigation } from '@utmb/design-system/use-dashboard-navigation/UseDashboardNavigation';
// Interfaces
import {
    BreadcrumbsView,
    GlobalEventView,
    GlobalFooterView,
    GlobalMobileBottomBarView,
    GlobalNavigationView,
    EventView,
    GlobalPartnersView,
    SeoView,
    GlobalNavigationMenuLevelView,
} from '@utmb/types/index';
// Styles
import styles from './layout-base.module.scss';

const cx = classNames.bind(styles);

export interface LayoutProps extends SeoView {
    breadcrumbs?: BreadcrumbsView;
    children: ReactNode;
    event: GlobalEventView;
    eventsTopBar: EventView[];
    languages: string[];
    footer: GlobalFooterView;
    isMobile: boolean;
    landing?: boolean;
    mobileBottomBar: GlobalMobileBottomBarView;
    mobileHeader?: boolean;
    navigation: GlobalNavigationView;
    raceTheme?: string;
    raceThemeIsDark?: boolean;
    translationSlugs?: {
        [key: string]: string;
    };
    partners?: GlobalPartnersView;
    menuLogoIsDark?: boolean;
}

const LayoutBase = (props: LayoutProps): ReactElement => {
    const {
        breadcrumbs,
        children,
        description,
        event,
        eventsTopBar,
        footer,
        image,
        keywords,
        languages,
        menuLogoIsDark,
        mobileBottomBar,
        partners,
        title,
        translationSlugs,
    } = props;

    const [pageUrl, setPageUrl] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const { getImage } = useCloudinary();
    const { isLoginMemberInProgress } = useDashboard();
    const { registrationNavigation } = useDashboardNavigation();

    // Reset Types for navigation
    const dashboardRoutes = registrationNavigation as unknown as GlobalNavigationMenuLevelView[];

    const isLoading = isLoginMemberInProgress || !registrationNavigation;

    const mobileNavigation: GlobalNavigationView = {
        children: dashboardRoutes || [],
    };

    const navigationProps = {
        breadcrumbs,
        mobileBottomBar,
        navigation: mobileNavigation,
        event,
        eventsTopBar,
        languages,
        translationSlugs,
        partners,
        menuLogoIsDark,
    };

    useEffect(() => {
        setPageUrl(window.location.href);
    }, []);

    const imageURL = getImage(image?.publicId);

    const containerCSS = cx('container-xxl', 'd-flex', 'flex-column', '-is-registration-page', styles.container);
    const triangleBGCSS = cx(styles.triangle_background, styles.triangle_background_registration, {
        triangle_background_skeleton: isLoading,
    });
    const rightContentCSS = cx(styles.right_content, {
        right_content: !isLoading,
        right_content_skeleton: isLoading,
    });

    return (
        <>
            <Head>
                <title>{title}</title>
                {/* <meta name="robots" content="noindex" /> */}
                <meta name="title" content={title} />
                <meta name="description" content={description} />
                <meta name="keywords" content={keywords} />
                <meta name="og:title" content={title} />
                <meta name="og:description" content={description} />
                <meta name="og:url" content={pageUrl} />
                <meta name="og:image" content={imageURL} />
                <meta name="og:type" content="website" />
                <meta name="twitter:url" content={pageUrl} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={imageURL} />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="canonical" href={pageUrl} />
            </Head>
            <SkipNavLink>Skip to Content</SkipNavLink>
            <ResizeObserver
                onResize={() => {
                    const isWindowMobile = window.innerWidth < parseInt(styles['bp-lg'], 10);
                    setIsMobile(isWindowMobile);
                }}
            />

            {isMobile ? (
                <>{isLoading ? <MobileSkeleton /> : <Mobile {...navigationProps} />}</>
            ) : (
                <TopBar eventName={event?.name} eventsTopBar={eventsTopBar} languages={languages} translationSlugs={translationSlugs} />
            )}

            <div className={containerCSS}>
                {isMobile ? (
                    <>
                        {isLoading ? (
                            <RegistrationSkeleton />
                        ) : (
                            <>
                                <MobileRegistrationNavigation />
                                {children}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className={triangleBGCSS}>
                            {isLoading ? (
                                <>
                                    <BrandSkeleton inMenu />
                                    <NavigationSkeleton isRegistrationPage />
                                </>
                            ) : (
                                <>
                                    <Navigation />
                                </>
                            )}
                        </div>
                        <div className={rightContentCSS}>{isLoading ? <RegistrationSkeleton /> : <SkipNavContent>{children}</SkipNavContent>}</div>
                    </>
                )}
            </div>

            <Footer {...footer} event={event} />
            <ToastContainer position="bottom-right" hideProgressBar transition={Slide} />
        </>
    );
};

export default LayoutBase;
